import { FormEvent, useEffect, useState, useMemo } from 'react'
import translate from 'core/translate'
import moment from 'moment'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import helpersBroadcasts from 'core/helpers/_broadcasts'
import { api } from 'core/api/ConnectionManager'
import { routes } from 'features/routes'
import { changeLocation } from 'features/routes'
import { reportInitializedNotification } from 'blocks.app/app/__notifications/app__notifications'
import { IReportState, reportActions } from './report.state'
import { useDispatch, useSelector } from 'react-redux'
import { emitError } from '../../features/appNotifications/AppNotifications.state'
import { IFile } from '../../core/models/Files'
import { IDevice } from '../../core/models/DigitalSignage'
import { IBroadcast } from '../../core/models/Broadcast'
import { IAdvertisingCampaign } from '../../core/models/AdvertisingCampaigns'
import { IRootState } from '../../core/store/rootReducer'
import { AppDispatch } from '../../core/store'

type Location = {
    pathname: string
    search: string
    hash: string
    state: unknown
}

const getReportType = (reportType: string, devicesReportType: string, tabSwitch?: boolean) => {
    switch (reportType) {
        case 'multipleSelect':
            return 'selectPlaybackContent'
        case 'files':
            return 'folderReport'
        case 'broadcasts':
            return 'broadcastReport'
        case 'advertisingCampaigns':
            return 'advertisingCampaignReport'
        case 'photoBoothSurveyReport':
            return 'photoBoothSurveyReport'
        case 'licenseReport':
            return 'licenseReport'
        case 'currencyReport':
            return 'currencyReport'
        case 'yandexWeatherReport':
            return 'yandexWeatherReport'
        case 'yandexJamReport':
            return 'yandexJamReport'
        case 'advertisingAds':
            return 'advertisingAdsReport'
        default:
            if (!devicesReportType) {
                return tabSwitch ? '' : 'digitalSignageReport'
            }
            return devicesReportType
    }
}

type IDate = {
    startDate: number
    endDate: number
    startTime: string
    endTime: string
}

type ReportPeriod = 'day' | 'week' | 'month' | 'other'
type ReportSelectedItem = IFile | IBroadcast | IDevice | IAdvertisingCampaign

interface IReportEntityWithId {
    id: number
}

interface IReportRequestData {
    startDate: string
    endDate: string
    email: boolean
    reportView: string
    folderId?: number[]
    sourceId?: number[]
    broadcastId?: number[]
    broadcastFolderId?: number[]
    digitalSignageId?: number[]
    groupId?: number[]
    advertisingCampaignId?: number[]
    advertisementIds?: number[]
    utcOffsetMinutes?: number
    reportType?: string
}

type IInitializedReportState = Omit<IReportState, 'startDate' | 'endDate' | 'startTime' | 'endTime'> & {
    startDate: number
    endDate: number
    startTime: string
    endTime: string
}

export const ReportMethodsHooks = (location: Location) => {
    const report = useSelector<IRootState, IInitializedReportState>((state) => {
        return state.report as IInitializedReportState
    })
    const locale = useSelector<IRootState, string>((state) => {
        return state.user.data?.settings?.locale || 'ru'
    })
    const dispatch = useDispatch<AppDispatch>()
    const [period, setPeriod] = useState<ReportPeriod>('day')
    const [selectedSubStep, setSelectedSubStep] = useState('displays')
    const [selectedStep, setSelectedStep] = useState('')

    useEffect(() => {
        const query = getURLSearchParamsByLocation(location)
        const reportType = query.reportType || 'displays'

        setSelectedStep(reportType)
    }, [location])

    useEffect(() => {
        if (selectedStep === 'multipleSelect' && report.reportView !== 'contentPlayQuantity') {
            dispatch(reportActions.setReportView('contentPlayQuantity'))
        }
        if (selectedStep !== 'multipleSelect' && report.reportView === 'contentPlayQuantity') {
            dispatch(reportActions.setReportView('default'))
        }

        dispatch(reportActions.setDevicesReportType(getReportType(selectedStep, '', true)))
    }, [selectedStep, report.reportView])

    useEffect(() => {
        dispatch(reportActions.setDevicesReportType(getReportType(selectedSubStep, '', true)))
    }, [selectedSubStep])

    const onSelectStep = (id: string) => {
        setSelectedStep(id)
    }

    const reports = useMemo(
        () => [
            {
                id: 'displays',
                name: translate('devices'),
            },
            {
                id: 'broadcasts',
                name: translate('broadcasts'),
            },
            {
                id: 'files',
                name: translate('files'),
            },
            {
                id: 'advertisingCampaigns',
                name: translate('advertisingCampaigns'),
            },
            {
                id: 'advertisingAds',
                name: translate('advertisingAds'),
            },
            {
                id: 'multipleSelect',
                name: translate('multipleSelect'),
            },
        ],
        [locale]
    )

    const subSteps = useMemo(
        () => [
            {
                id: 'displays',
                name: translate('devices'),
            },
            {
                id: 'broadcasts',
                name: translate('broadcasts'),
            },
            {
                id: 'files',
                name: translate('files'),
            },
        ],
        [locale] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const onSelectSubStep = (id: string) => {
        setSelectedSubStep(id)
    }

    const clearData = () => dispatch(reportActions.clearData())
    const setDate = (data: IDate) => dispatch(reportActions.setDate(data))
    const setStartDate = (data: number) => dispatch(reportActions.setStartDate(data))
    const setEndDate = (data: number) => dispatch(reportActions.setEndDate(data))
    const setStartTime = (data: string) => dispatch(reportActions.setStartTime(data))
    const setEndTime = (data: string) => dispatch(reportActions.setEndTime(data))
    const setMailReport = (data: boolean) => dispatch(reportActions.setMailReport(data))
    const setDevicesReportType = (data: string) => dispatch(reportActions.setDevicesReportType(data))
    const setReportFiles = (data: IFile[]) => dispatch(reportActions.setFiles(data))
    const setReportBroadcasts = (data: IBroadcast[]) => dispatch(reportActions.setBroadcasts(data))
    const setReportAdvertisingCampaigns = (data: IAdvertisingCampaign[]) =>
        dispatch(reportActions.setAdvertisingCampaigns(data))
    const setReportDevices = (data: IDevice[]) => dispatch(reportActions.setDevices(data))
    const setReportAdvertisingAds = (data: IAdvertisingCampaign[]) => dispatch(reportActions.setAdvertisingAds(data))
    const showError = (err: string) => dispatch(emitError(err))
    const setReportView = (data: string) => dispatch(reportActions.setReportView(data))

    const getTitle = (location: Location) => {
        const query = getURLSearchParamsByLocation(location)
        const reportType = query.reportType || 'displays'

        switch (reportType) {
            case 'files':
                return translate('advFilesEmpty')
            case 'broadcasts':
                return translate('advBroadcastsEmpty')
            case 'advertisingCampaigns':
                return translate('advReportEmpty')
            case 'advertisingAds':
                return translate('advertisingAdsReportEmpty')
            default:
                return translate('advDevicesEmpty')
        }
    }

    const getMultiSelectTitle = (reportType: string) => {
        switch (reportType) {
            case 'files':
                return translate('advFilesEmpty')
            case 'broadcasts':
                return translate('advBroadcastsEmpty')
            case 'advertisingCampaigns':
                return translate('advReportEmpty')
            case 'advertisingAds':
                return translate('advertisingAdsReportEmpty')
            default:
                return translate('advDevicesEmpty')
        }
    }

    const selectPeriod = (selected: { id: ReportPeriod }) => {
        const periods: Array<Exclude<ReportPeriod, 'other'>> = ['day', 'week', 'month']
        const period = selected.id

        if (period !== 'other' && periods.includes(period)) {
            setDate({
                startDate: moment().subtract(1, period).toDate().getTime(),
                endDate: moment().toDate().getTime(),
                startTime: moment().startOf('day').format('HH:mm:ss'),
                endTime: moment().endOf('day').format('HH:mm:ss'),
            })
        }

        setPeriod(period)
    }
    const submit = (action: { e: FormEvent<HTMLFormElement>; reportType: string }) => {
        const {
            startDate,
            endDate,
            startTime,
            endTime,
            advertisingCampaigns,
            devices,
            advertisingAds,
            devicesReportType,
            files,
            broadcasts,
            mailReport,
            reportView,
        } = report

        action.e.preventDefault()

        let data: IReportRequestData = {
            startDate: `${moment(startDate).format('YYYY-MM-DD')} ${startTime}`,
            endDate: `${moment(endDate).format('YYYY-MM-DD')} ${endTime}`,
            email: mailReport,
            reportView,
        }

        if (action.reportType === 'multipleSelect') {
            const multiSelectData = {
                ...data,
                folderId: files.filter((file: IFile) => file.isDirectory).map((file: IFile) => file.id),
                sourceId: files.filter((file: IFile) => !file.isDirectory).map((file: IFile) => file.id),
                ...getIdsFromBroadcasts(broadcasts),
                ...getDsFromDevices(devices),
                reportType: getReportType(action.reportType, devicesReportType),
            }

            if (
                !(multiSelectData.sourceId.length || multiSelectData.folderId.length) ||
                !(multiSelectData.broadcastId.length || multiSelectData.broadcastFolderId.length) ||
                !(multiSelectData.digitalSignageId.length || multiSelectData.groupId.length)
            ) {
                showError('necessaryElementsNotSelected')

                return
            }

            api.send('createReportTask', multiSelectData).then(() => {
                changeLocation(`/${routes.reportTasks.path}`)
                clearData()
            })

            return
        }

        if (validate(action.reportType)) {
            switch (action.reportType) {
                case 'files':
                    data = {
                        ...data,
                        folderId: files.filter((file: IFile) => file.isDirectory).map((file: IFile) => file.id),
                        sourceId: files.filter((file: IFile) => !file.isDirectory).map((file: IFile) => file.id),
                    }
                    break
                case 'broadcasts':
                    data = { ...data, ...getIdsFromBroadcasts(broadcasts) }
                    break
                case 'advertisingCampaigns':
                    data = { ...data, advertisingCampaignId: getEntityIds(advertisingCampaigns) }
                    break
                case 'photoBoothSurveyReport':
                    data = { ...data, utcOffsetMinutes: moment().utcOffset() }
                    break
                case 'licenseReport':
                    data = { ...data }
                    break
                case 'currencyReport':
                    data = { ...data, utcOffsetMinutes: moment().utcOffset() }
                    break
                case 'advertisingAds':
                    data = { ...data, ...getAdvertisingAdsIds(advertisingAds), utcOffsetMinutes: moment().utcOffset() }
                    break
                default:
                    data = { ...data, ...getDsFromDevices(devices) }
                    break
            }

            if (devicesReportType === 'totalCostReport') {
                return reportInitializedNotification()
            }

            api.send('createReportTask', {
                ...data,
                reportType: getReportType(action.reportType, devicesReportType),
            }).then(() => {
                changeLocation(`/${routes.reportTasks.path}`)
                clearData()
            })
        }
    }
    const validate = (reportType: string) => {
        const { files, broadcasts, advertisingCampaigns, devices, advertisingAds } = report
        switch (reportType) {
            case 'files':
                if (files.length === 0) {
                    showError('advFilesEmpty')
                }

                return files.length > 0
            case 'broadcasts':
                if (broadcasts.length === 0) {
                    showError('advBroadcastsEmpty')
                }

                return broadcasts.length > 0
            case 'advertisingCampaigns':
                if (!advertisingCampaigns.length) {
                    showError('advReportEmpty')
                }
                return !!advertisingCampaigns.length
            case 'photoBoothSurveyReport':
            case 'licenseReport':
            case 'currencyReport':
            case 'yandexWeatherReport':
            case 'yandexJamReport':
                return true
            case 'advertisingAds':
                if (advertisingAds.length === 0) {
                    showError('advertisingAdsReportEmpty')

                    return false
                }
                return true
            default:
                if (devices.length === 0) {
                    showError('advDevicesEmpty')
                }

                return devices.length > 0
        }
    }
    const getDsFromDevices = (devices: IDevice[]) => {
        const groupId: number[] = []
        const digitalSignageId: number[] = []

        devices.forEach((device) => {
            if (device.type === 'group') {
                groupId.push(device.id)
            } else {
                digitalSignageId.push(device.id)
            }
        })

        return { groupId, digitalSignageId }
    }
    const getEntityIds = (list: IAdvertisingCampaign[]): number[] => {
        return list.flatMap((item) => {
            const entity = item as Partial<IReportEntityWithId>

            return typeof entity.id === 'number' ? [entity.id] : []
        })
    }
    const getAdvertisingAdsIds = (list: IAdvertisingCampaign[]) => {
        return { advertisementIds: getEntityIds(list) }
    }
    const getIdsFromBroadcasts = (broadcasts: IBroadcast[]) => {
        const broadcastFolderId: number[] = []
        const broadcastId: number[] = []

        broadcasts.forEach((broadcast) => {
            if (helpersBroadcasts.isFolder(broadcast)) {
                broadcastFolderId.push(broadcast.id)
            } else {
                broadcastId.push(broadcast.id)
            }
        })

        return { broadcastFolderId, broadcastId }
    }
    const setReportItems = (reportType: string, selectedItems: ReportSelectedItem[]) => {
        switch (reportType) {
            case 'broadcasts':
                return setReportBroadcasts(selectedItems as IBroadcast[])
            case 'files':
                return setReportFiles(selectedItems as IFile[])
            case 'advertisingCampaigns':
                return setReportAdvertisingCampaigns(selectedItems as IAdvertisingCampaign[])
            case 'advertisingAds':
                return setReportAdvertisingAds(selectedItems as IAdvertisingCampaign[])
            default:
                return setReportDevices(selectedItems as IDevice[])
        }
    }

    const getReportItems = (reportType: string): ReportSelectedItem[] => {
        switch (reportType) {
            case 'broadcasts':
                return report.broadcasts
            case 'files':
                return report.files
            case 'advertisingCampaigns':
                return report.advertisingCampaigns
            case 'advertisingAds':
                return report.advertisingAds
            default:
                return report.devices
        }
    }

    const getSelectTypeRequestOptions = (step: string) => {
        switch (step) {
            case 'broadcasts':
                return {
                    byDigitalSignage: {
                        digitalSignageIds: report.devices
                            .filter((display: IDevice) => display.type === 'digitalSignage')
                            .map((display: IDevice) => display.id),
                        groupIds: report.devices
                            .filter((display: IDevice) => display.type === 'group')
                            .map((display: IDevice) => display.id),
                    },
                }
            case 'files':
                return {
                    byBroadcast: {
                        broadcastIds: report.broadcasts
                            .filter((broadcast: IBroadcast) => broadcast.instanceType === 'broadcast')
                            .map((broadcast: IBroadcast) => broadcast.id),
                        broadcastFolderIds: report.broadcasts
                            .filter((broadcast: IBroadcast) => broadcast.instanceType === 'broadcastFolder')
                            .map((broadcast: IBroadcast) => broadcast.id),
                    },
                }
            default:
                return null
        }
    }

    useEffect(() => {
        clearData()
        selectPeriod({ id: 'week' })
    }, [])

    return {
        reports,
        report,
        period,
        setStartDate,
        setEndDate,
        setStartTime,
        setEndTime,
        setMailReport,
        setDevicesReportType,
        setReportItems,
        getReportItems,
        submit,
        getTitle,
        setPeriod,
        selectPeriod,
        subSteps,
        onSelectSubStep,
        selectedSubStep,
        onSelectStep,
        selectedStep,
        getMultiSelectTitle,
        setReportView,
        getSelectTypeRequestOptions,
    }
}
